import { useEffect, useRef, useState } from 'react';
import { CANADA_BBOX, fetchAnomalyCounts, fetchAssistanceRequests } from '../api';
import { openAssistCount } from '../AssistQueueList';

/**
 * Update signals for the operations-panel queue tabs (control-panel spec §2).
 *
 * Each field is a MONOTONIC counter that ticks up once whenever that queue gets
 * an update — it is NOT the row count. The side-by-side queue columns watch these
 * counters: a tick fires a brief header pulse on that column (see
 * `useQueuePulse.ts` / `PanelQueueColumn.tsx`).
 *
 * Two update sources feed the counters, decoupled from the heavy queue panels:
 *   • a light national-count poll (anomaly counts + open-assist count) — a rise
 *     in either national count is a real "new work arrived" tick; and
 *   • a `tsr:queue-update` window CustomEvent — a reusable in-app bus a producer
 *     (or a verification harness) can fire to force a tick:
 *       window.dispatchEvent(new CustomEvent('tsr:queue-update',
 *         { detail: { queue: 'alert' | 'assist' } }))
 */

export interface QueueSignals {
  alert: number;
  assist: number;
}

export const QUEUE_UPDATE_EVENT = 'tsr:queue-update';

const POLL_MS = 30_000;

export function useQueueSignals(): QueueSignals {
  const [signals, setSignals] = useState<QueueSignals>({ alert: 0, assist: 0 });
  // Last national count seen per queue — a rise is what ticks the signal. null
  // until the first poll so the initial load doesn't read as an "update".
  const lastAlert = useRef<number | null>(null);
  const lastAssist = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async (): Promise<void> => {
      const ctl = new AbortController();
      try {
        const [counts, assist] = await Promise.all([
          fetchAnomalyCounts(CANADA_BBOX, ctl.signal, { minConfidence: 60 }),
          fetchAssistanceRequests({ limit: 50 }, ctl.signal),
        ]);
        if (cancelled) return;
        const alertCount = counts.total;
        const assistCount = openAssistCount(assist.requests);
        setSignals((prev) => {
          let { alert, assist: as } = prev;
          if (lastAlert.current !== null && alertCount > lastAlert.current) alert += 1;
          if (lastAssist.current !== null && assistCount > lastAssist.current) as += 1;
          lastAlert.current = alertCount;
          lastAssist.current = assistCount;
          return alert === prev.alert && as === prev.assist ? prev : { alert, assist: as };
        });
      } catch {
        // A failed poll just skips a tick; the next interval retries.
      }
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_MS);

    const onEvent = (e: Event): void => {
      const queue = (e as CustomEvent<{ queue?: string }>).detail?.queue;
      setSignals((prev) => {
        if (queue === 'alert') return { ...prev, alert: prev.alert + 1 };
        if (queue === 'assist') return { ...prev, assist: prev.assist + 1 };
        return prev;
      });
    };
    window.addEventListener(QUEUE_UPDATE_EVENT, onEvent);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener(QUEUE_UPDATE_EVENT, onEvent);
    };
  }, []);

  return signals;
}
